import clsx from 'clsx';
import { type FC, type MouseEvent, type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductImage } from 'src/components/ProductImage';
import Button from 'src/components/design-system/Button';
import { SupplierCatalog } from 'src/types/view_catalogs/SupplierCatalog';
import { DEFAULT_PLACEHOLDERS, getProductImageSrc } from 'src/utils/parseProductImageUrl';
import { isOfflineCatalog } from './ViewCatalogsUtils';

const PUNCHOUT_CATALOG_TYPE = 2;

interface SupplierCatalogCardProps {
  supplier: SupplierCatalog;
  onBrowseCatalog: (supplier: SupplierCatalog) => void;
  onOpenPunchout: (supplier: SupplierCatalog) => void;
  onSupplierInfo: (supplier: SupplierCatalog) => void;
  onProgramInfo: (supplier: SupplierCatalog) => void;
  isPunchoutLoading?: boolean;
}

const SupplierCatalogCard: FC<SupplierCatalogCardProps> = ({
  supplier,
  onBrowseCatalog,
  onOpenPunchout,
  onSupplierInfo,
  onProgramInfo,
  isPunchoutLoading = false,
}): ReactElement => {
  const { t } = useTranslation();
  const logoSrc = useMemo(
    () => getProductImageSrc(supplier.image) || DEFAULT_PLACEHOLDERS.noImage,
    [supplier.image]
  );

  const isPunchout = supplier.catalogType === PUNCHOUT_CATALOG_TYPE;
  const isOffline = isOfflineCatalog(supplier.catalogType);
  const hasProgramInfo = !!supplier.programInfo;
  const showSupplierInfo = !isOffline;
  const showFooter = showSupplierInfo || hasProgramInfo;

  const handlePrimaryAction = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isPunchout) {
      onOpenPunchout(supplier);
      return;
    }
    onBrowseCatalog(supplier);
  };

  const handleLinkClick =
    (handler: (supplier: SupplierCatalog) => void) => (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handler(supplier);
    };

  return (
    <article
      className={clsx(
        'flex h-[240px] w-full flex-col gap-3 rounded-sm border border-[#D1D1D1] bg-white p-3 shadow-sm',
        'sm:max-w-[270px] md:max-w-[290px]'
      )}
    >
      <div className="flex h-[80px] w-full shrink-0 items-center justify-center overflow-hidden">
        <ProductImage
          productKey={`supplier-catalog-image-${supplier.catalogId}`}
          productId={supplier.catalogId}
          supplierId={supplier.catalogId}
          linkUrl={logoSrc}
          altText={supplier.catalogName}
          type="summary"
          imageHeight="80px"
          imageWidth="100%"
          imageClassName="!h-full !w-full !max-h-[80px] !object-contain mx-auto bg-transparent"
        />
      </div>

      <div className="flex w-full flex-col gap-2 text-center">
        <h3
          className="w-full truncate text-sm font-medium leading-tight text-[#111111] sm:text-base"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={supplier.catalogName}
        >
          {supplier.catalogName}
        </h3>

        <p
          className="w-full truncate text-xs font-normal leading-tight text-[#4A5565]"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={supplier.accountNumber}
        >
          {t('Account #')}: {supplier.accountNumber ?? 'N/A'}
        </p>
      </div>

      <Button
        type="button"
        color="info"
        size="lg"
        className="!m-0 w-full shrink-0 justify-center font-medium"
        onClick={handlePrimaryAction}
        loading={isPunchout && isPunchoutLoading}
      >
        {isPunchout ? t('Open Punchout') : t('Browse Catalog')}
      </Button>

      {showFooter && (
        <div className="flex w-full shrink-0 items-center justify-center gap-3 text-xs leading-none sm:text-sm">
          {showSupplierInfo && (
            <button
              type="button"
              className="p-0 font-normal leading-none text-info hover:underline focus:outline-2 focus:outline-black"
              onClick={handleLinkClick(onSupplierInfo)}
              aria-label={`${t('Supplier Info')} - ${supplier.catalogName}`}
            >
              {t('Supplier Info')}
            </button>
          )}
          {showSupplierInfo && hasProgramInfo && (
            <span className="text-[#AAAAAA]" aria-hidden="true">
              |
            </span>
          )}
          {hasProgramInfo && (
            <button
              type="button"
              className="p-0 font-normal leading-none text-info hover:underline focus:outline-2 focus:outline-black"
              onClick={handleLinkClick(onProgramInfo)}
              aria-label={`${t('Program Info')} - ${supplier.catalogName}`}
            >
              {t('Program Info')}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export default SupplierCatalogCard;
